import ConfigurableColumnBuilder from "common/agGrid/configurableColumnBuilder";
import { formatDecimalPlace, formatQuantity, formatOrderDisplayId, formatPricingValue } from "common/formatters";
import { getProducerDisplayName } from "features/settings/producers/producer";
import { ColumnCustomization } from "common/agGrid/columnCustomization";
import Contract, { getContractTypeName } from "domain/contract";
import React, { useRef } from "react";
import SiteLink from "common/ui/SiteLink";
import { getDeliveryPeriodDisplayName } from "domain/deliveryPeriod";
import { compareDisplayIds } from "common/strings";
import { useAnyPermission } from "common/security/usePermission";
import Permissions from "common/security/permissions";
import { canShowOrderId } from "features/offers/orderStatus";
import { Form } from "react-bootstrap";
import useUniqueId from "common/ui/useUniqueId";
import { getTotalFees } from "./helpers"
import { getProducerAccountDisplayName } from "features/settings/producers/producerAccount";
import contractTypeHelper from "domain/contractTypeHelper";
import { UnitConversionRate } from "domain/unit";
import unitHelper from "domain/unitHelper";
import { TranslatorCollection } from "common/translation/useTranslators";
import { isAgrisErp } from "domain/erpSystem";
import Merchant from "domain/merchant";
import BasisColumn from "features/offers/BasisColumn";
import ProducerColumn from "features/settings/producers/ProducerColumn";
import { localizeOfferContract as localize, localizeCommon } from "features/localization/localizeService";
import { isErpIdSyncing } from "common/formatters";
import UiCustomization from 'domain/uiCustomization';
import * as NP from "number-precision";
import { getContractSignatureStatusText, getContractSignatureStatusTextClass } from "domain/contractSignatureStatus";
import classNames from "classnames";
import ESignStatus from "features/offers/ESignStatus";
import { CustomerWillSellText } from "common/formatters/useCustomerWillSellText";

export class ContractColumn {
    //These strings should never change after publishing.
    //Otherwise it will invalidate user settings
    public static id: string = "id";
    public static customerContractId: string = "customerContractId";
    public static flag: string = "flag";
    public static customer: string = "customer";
    public static customerAccount: string = "customerAccount";
    public static commodity: string = "commodity";
    public static position: string = "Position";
    public static location: string = "location";
    public static inventoryLocation: string = "inventoryLocation";
    public static contractLocation: string = "contractLocation";
    public static customerWillSell: string = "customerWillSell";
    public static quantity: string = "quantity";
    public static uom: string = "uom";
    public static localQuantity: string = "localQuantity";
    public static appliedQuantity: string = "appliedQuantity";
    public static unappliedQuantity: string = "unappliedQuantity";
    public static localUom: string = "localUom";
    public static cashPrice: string = "cashPrice";
    public static finalPrice: string = "finalPrice";
    public static basis: string = "basis";
    public static contractSymbol: string = "contractSymbol";
    public static deliveryEnd: string = "deliveryEnd";
    public static trader: string = "trader";
    public static lastUpdatedDate: string = "lastUpdatedDate";
    public static lastUpdatedBy: string = "lastUpdatedBy";
    public static deliveryPeriod: string = "deliveryPeriod";
    public static totalFee: string = "totalFee";
    public static deliveryStart: string = "deliveryStart";
    public static createdDate: string = "createdDate";
    public static createdBy: string = "createdBy";
    public static finalPricingDate: string = "contractExpiration"; //Column used to be called "contractExpiration", needed for backward compatibility
    public static comments: string = "comments";
    public static deliveryMethod: string = "deliveryMethod";
    public static freightFee: string = "freightFee";
    public static workingQuantity: string = "workingQuantity";
    public static remainingQuantity: string = "remainingQuantity";
    public static erpId: string = "erpId";
    public static eSignStatus: string = "eSignStatus";
    public static orderId: string = "orderId";
    public static fillPrice: string = "fillPrice";
    public static futuresPrice: string = "futuresPrice";
    public static actualBasis: string = "actualBasis";
    public static myBasis: string = "myBasis";
    public static netBasis: string = "netBasis";
    public static pushValue: string = "pushValue";
    public static fillTime: string = "fillTime";
    public static producerAccount: string = "producerAccount";
    public static paymentOption: string = "paymentOption";
    public static contractType: string = "contractType";
    public static currency: string = "currency";
    public static contractExtendedType: string = "extendedType";

    /* Mobile-specific columns */
    public static remainingQuantity_mobile: string = "remainingQuantity_mobile";
}

export class ContractListColumnsFilter {
    isCustomerEditAllowed: boolean = false;
    onCustomerSelected: any = null;
    onSignatureRequested: any = null;
    isCustomer: boolean = false;
    isEsignEnabled: boolean = false;
    isMobile: boolean = false;
    conversionRates: UnitConversionRate[] = [];
    translators: TranslatorCollection = null;
    onContractFlagUpdated?: any = null;
    onErpIdSelected?: any = null;
    showQuantityUnits: boolean = false;
    merchant: Merchant = null;
}

export function getColumnBuilder(filter: ContractListColumnsFilter, uiCustomization: UiCustomization, customerWillSellText: CustomerWillSellText, showPushBasis: boolean): ConfigurableColumnBuilder<Contract> {
    const builder = new ConfigurableColumnBuilder<Contract>();
    const defaultClass = filter.isMobile ? "mobile" : "";
    const quantityPrecision = uiCustomization.offersContractsQuantityDecimalPrecision;

    //Default columns
    builder.for(ContractColumn.id, localize("Column_Header_ContractId"), "displayId", localize("Column_Description_ContractId"))
        .withCellRenderer(params => <SiteLink.ContractDetails contractDisplayId={params.value} />)
        .withComparator(compareDisplayIds)
        .withColumnClass(defaultClass, defaultClass)
        .withWidth(filter.isMobile ? 55 : 95);
    builder.for(ContractColumn.customerContractId, localize("Details_Label_CustomerContractId"), "customerContractId", localize("Column_Description_CustomerContractId")
        , { hide: !filter.merchant.generalSettings.displayCustomerContractIdForBuy && !filter.merchant.generalSettings.displayCustomerContractIdForSell })
        .withWidth(135);
    builder.for(ContractColumn.flag, localize("Column_Header_Flag"), "flag", localize("Column_Description_Flag"), { hide: filter.isCustomer, width: 30 })
        .withCellRenderer(params => <FlagCheckbox
            isChecked={params.value}
            onChange={isChecked => filter.onContractFlagUpdated({ ...params.node.data, flag: isChecked })}
            hide={params.node.group}
        />),
        builder.for(ContractColumn.customer, localize("Column_Header_Customer"), "", "", { hide: filter.isCustomer })
            .withGetter(params => getProducerDisplayName(params.data?.producer))
            .withCellRenderer(params => <ProducerColumn name={params.value}
                isEditAllowed={filter.isCustomerEditAllowed}
                isActive={params.node.data.producer.isActive}
                onSelected={() => filter.onCustomerSelected(params.node.data.producer)} />)
            .withWidth(150);

    builder.for(ContractColumn.commodity, localize("Column_Header_Commodity"), "commodity.name")
        .withWidth(100);
    builder.for(ContractColumn.position, localize("Column_Header_Position"), "positionSetting.displayName", localize("Column_Description_Position"), { hide: filter.isCustomer })
        .withWidth(80);
    builder.for(ContractColumn.location, localize("Column_Header_Location"), "location.name", localize("Column_Description_Location"))
        .withFormatter(m => m ?? localizeCommon("Value_DetermineLater"))
        .withWidth(150);
    builder.for(ContractColumn.inventoryLocation, localize("Column_Header_InventoryLocation"), "inventoryLocation.name", localize("Column_Description_InventoryLocation"), { hide: !filter.merchant.generalSettings.displayInventoryLocation })
        .withFormatter(m => m ?? localizeCommon("Value_DetermineLater"))
        .withWidth(150);
    builder.for(ContractColumn.contractLocation, localize("Column_Header_ContractLocation"), "contractLocation.name", localize("Column_Description_ContractLocation"), { hide: !filter.merchant.generalSettings.displayContractLocation })
        .withFormatter(m => m ?? localizeCommon("Value_DetermineLater"))
        .withWidth(150);
    builder.for(ContractColumn.customerWillSell, customerWillSellText.columnHeader, "customerWillSell", customerWillSellText.columnDescription)
        .withFormatter(customerWillSellText.formatValue)
        .withWidth(60);

    builder.for(ContractColumn.quantity, localize("Column_Header_Quantity"), "quantityContracted", localize("Column_Description_ContractQuantity"), { aggFunc: "npSum" })
        .withFormatterParams(params => !filter.showQuantityUnits
            ? formatQuantity(params.value, quantityPrecision)
            : unitHelper.getQuantityTextWithUnits(params.value, params.data?.commodity, filter.conversionRates, quantityPrecision))
        .withTooltipValueGetter(params => !filter.showQuantityUnits && params.data != null
            ? unitHelper.getLocalQuantityTextWithUnits(params.value, params.data.commodity, filter.conversionRates, quantityPrecision)
            : null)
        .withColumnClass(defaultClass, defaultClass)
        .withWidth(filter.isMobile ? 50 : 120);
    builder.forExportOnly(ContractColumn.uom, localize("Column_Header_BaseUnit"))
        .withWidth(30);
    builder.forExportOnly(ContractColumn.localQuantity, localize("Column_Header_LocalQuantity"))
        .withWidth(120);
    builder.forExportOnly(ContractColumn.localUom, localize("Column_Header_LocalUnit"))
        .withWidth(30);
    builder.for(ContractColumn.cashPrice, localize("Column_Header_CashPrice"), "cashPrice", localize("Column_Description_ContractCashPrice"))
        .withGetter(params => unitHelper.getLocalPriceText(params.data?.cashPrice, params.data?.commodity, filter.conversionRates))
        .asRightAligned()
        .withColumnClass(defaultClass, defaultClass)
        .withWidth(filter.isMobile ? 55 : 120);
    builder.for(ContractColumn.finalPrice, localize("Column_Header_FinalPrice"), "finalPrice", localize("Column_Description_ContractFinalPrice"))
        .withGetter(params => unitHelper.getLocalPriceText(params.data?.finalPrice, params.data?.commodity, filter.conversionRates))
        .asRightAligned()
        .withColumnClass(defaultClass, defaultClass)
        .withWidth(filter.isMobile ? 55 : 80);
    builder.for(ContractColumn.totalFee, localize("Column_Header_TotalFees"), "", "Total Fees")
        .withGetter(params => getTotalFees(params.node.data))
        .withTooltipValueGetter(params => params.data != null ? unitHelper.getLocalPrice(getTotalFees(params.data), params.data.commodity, filter.conversionRates) : null)
        .asDecimal()
        .asRightAligned()
        .withWidth(60);

    builder.for(ContractColumn.workingQuantity, localize("Column_Header_WorkingQuantity"), "workingQuantity", localize("Column_Description_WorkingQuantity"), { aggFunc: "npSum" })
        .withFormatterParams(params => formatQuantity(params.value, quantityPrecision))
        .withTooltipValueGetter(params => params.data != null
            ? unitHelper.getLocalQuantityTextWithUnits(params.data.workingQuantity, params.data.commodity, filter.conversionRates, quantityPrecision)
            : null)
        .withWidth(120);

    if (isAgrisErp(filter.merchant.erpSystem)) {
        builder.for(ContractColumn.appliedQuantity, localize("Column_Header_AppliedQuantity"), "quantityDelivered", localize("Column_Description_AppliedQuantity"), { aggFunc: "npSum" })
            .withFormatterParams(params => formatQuantity(params.value, quantityPrecision))
            .withTooltipValueGetter(params => params.data != null
                ? unitHelper.getLocalQuantityTextWithUnits(params.data.quantityDelivered, params.data.commodity, filter.conversionRates, quantityPrecision)
                : null)
            .withWidth(120);
        builder.for(ContractColumn.unappliedQuantity, localize("Column_Header_UnappliedQuantity"), "", localize("Column_Description_UnappliedQuantity"), { aggFunc: "npSum" })
            .withGetter(params => params.node.data.quantityContracted - params.node.data.quantityDelivered)
            .withFormatterParams(params => formatQuantity(params.value, quantityPrecision))
            .withTooltipValueGetter(params => params.data != null
                ? unitHelper.getLocalQuantityTextWithUnits((params.node.data.quantityContracted - params.node.data.quantityDelivered), params.data.commodity, filter.conversionRates, quantityPrecision)
                : null)
            .withWidth(120);
    }

    if (filter.isMobile) {
        builder.for(ContractColumn.remainingQuantity_mobile, localize("Column_Header_UnpricedQuantityMobile"), "remainingQuantity", localize("Column_Description_UnpricedQuantity"))
            .withFormatterParams(params => formatQuantity(params.value, quantityPrecision))
            .withColumnClass(defaultClass, defaultClass)
            .withWidth(50);
    }
    else {
        builder.for(ContractColumn.remainingQuantity, localize("Column_Header_UnpricedQuantity"), "remainingQuantity", localize("Column_Description_UnpricedQuantity"), { aggFunc: "npSum" })
            .withFormatterParams(params => formatQuantity(params.value, quantityPrecision))
            .withTooltipValueGetter(params => params.data != null
                ? unitHelper.getLocalQuantityTextWithUnits(params.data.remainingQuantity, params.data.commodity, filter.conversionRates, quantityPrecision)
                : null)
            .withWidth(120);
    }

    builder.for(ContractColumn.currency, localize("Column_Header_Currency"), "contractCurrency.code", localize("Column_Description_Currency"))
        .asRightAligned()
        .withWidth(filter.isMobile ? 55 : 80);
    builder.for(ContractColumn.basis, localize("Column_Header_Basis"), "basis", localize("Column_Description_Basis"))
        .withFormatter(formatPricingValue)
        .asRightAligned()
        .withColumnClass(defaultClass, defaultClass)
        .withWidth(filter.isMobile ? 50 : 60);
    builder.for(ContractColumn.contractSymbol, localize("Column_Header_FuturesSymbol"), "futuresContract.symbol", localize("Column_Description_FuturesSymbol"))
        .withColumnClass(defaultClass, defaultClass)
        .withWidth(50);
    builder.for(ContractColumn.deliveryPeriod, localize("Column_Header_DeliveryPeriod"), "deliveryPeriod.name", localize("Column_Description_DeliveryPeriod"))
        .withGetter(params => getDeliveryPeriodDisplayName(params.data?.deliveryPeriod))
        .withWidth(195);
    builder.for(ContractColumn.deliveryStart, localize("Column_Header_DeliveryStart"), "deliveryPeriodStartDate", localize("Column_Description_DeliveryStart"))
        .asUtcDate()
        .withWidth(90);
    builder.for(ContractColumn.deliveryEnd, localize("Column_Header_DeliveryEnd"), "deliveryPeriodEndDate", localize("Column_Description_DeliveryEnd"))
        .asUtcDate()
        .withWidth(90);
    builder.for(ContractColumn.trader, localize("Column_Header_Trader"), "trader.lastName")
        .withWidth(100);

    builder.for(ContractColumn.createdBy, localize("Column_Header_CreatedBy"), "createdBy.name", localize("Column_Description_CreatedBy"))
        .withWidth(150);
    builder.for(ContractColumn.createdDate, localize("Column_Header_CreatedDate"), "createdDate", localize("Column_Description_CreatedDate"))
        .asDateTime()
        .withWidth(150);
    builder.for(ContractColumn.lastUpdatedBy, localize("Column_Header_ModifiedBy"), "lastUpdatedBy.name", "")
    builder.for(ContractColumn.lastUpdatedDate, localize("Column_Header_LastUpdate"), "lastUpdatedDate", localize("Column_Description_LastUpdate"))
        .asDateTime()
        .withWidth(150);
    builder.for(ContractColumn.finalPricingDate, localize("Column_Header_FinalPricingDate"), "finalPricingDate", localize("Column_Description_FinalPricingDate"))
        .asUtcDate()
        .withWidth(70);
    builder.for(ContractColumn.comments, localize("Column_Header_Comment"), "comments", localize("Column_Description_Comment"))
        .withWidth(150);
    builder.for(ContractColumn.contractExtendedType, localize("Column_Header_ContractExtendedType"), "contractExtendedType.name", localize("Column_Description_ContractExtendedType"))
        .withWidth(120);
    builder.for(ContractColumn.deliveryMethod, localize("Column_Header_DeliveryMethod"), "deliveryMethod", localize("Column_Description_DeliveryMethod"), { hide: filter.isCustomer })
        .withGetter(params => filter.translators.deliveryMethods.translate(params.data?.deliveryMethod))
        .withWidth(90);
    builder.for(ContractColumn.freightFee, localize("Column_Header_FreightFee"), "freightFee", localize("Column_Description_FreightFee"), { hide: filter.isCustomer })
        .withFormatter(formatPricingValue)
        .asRightAligned()
        .withWidth(80);
    builder.for(ContractColumn.fillTime, localize("Column_Header_OfferFillTime"), "offer.fillTime", localize("Column_Description_OfferFillTime"), { hide: filter.isCustomer })
        .asDateTime()
        .withWidth(150);
    builder.for(ContractColumn.futuresPrice, localize("Column_Header_RequiredFuturesPrice"), "futuresPrice", localize("Column_Description_RequiredFuturesPrice"))
        .withFormatter(formatPricingValue)
        .withColumnClass(defaultClass, defaultClass)
        .withWidth(filter.isMobile ? 60 : 80);
    builder.for(ContractColumn.actualBasis, localize("Column_Header_ActualBasis"), "basis", localize("Column_Description_ActualBasis"))
        .asRightAligned()
        .withCellRenderer(params => <BasisColumn value={params.value} isFixed={params.node.data.offer.isBasisFixed} />)
        .withWidth(80);
    builder.for(ContractColumn.myBasis, localize("Column_Header_PostedBasis"), null, localize("Column_Description_PostedBasis"))
        .withGetter(params => contractTypeHelper.isHta(params.node.data.contractType) ? null : params.node.data.offer?.currentBasis)
        .withFormatter(m => m == null ? "--" : formatDecimalPlace(m))
        .asRightAligned()
        .withWidth(100);
    builder.for(ContractColumn.netBasis, localize("Column_Header_NetBasis"), null, localize("Column_Description_NetBasis"))
        .withGetter(params => !contractTypeHelper.hasBasis(params.node.data.contractType) || params.node.data.commodity.isCashOnly ? null :
            NP.minus(params.node.data.basis ?? 0, params.node.data.freightFee ?? 0))
        .withFormatter(formatPricingValue)
        .asRightAligned()
        .withWidth(100);
    builder.for(ContractColumn.pushValue, localize("Column_Header_PushValue"), "pushedBasis", localize("Column_Description_PushValue"), { headerTooltip: localize("Column_HoverText_PushValue"), hide: !showPushBasis })
        .withFormatter(formatPricingValue)
        .asRightAligned()
        .withWidth(80);
    builder.for(ContractColumn.orderId, localize("Column_Header_HedgeOrderId"), "offer.order.displayId", localize("Column_Description_HedgeOrderId"), { hide: filter.isCustomer })
        .withGetter(params => canShowOrderId(params.data?.offer?.order?.status) ? params.data?.offer?.order?.displayId : "")
        .withFormatter(formatOrderDisplayId)
        .withTooltipValueGetter(params => canShowOrderId(params.data?.offer?.order?.status) ? params.data?.offer?.order?.displayId : "")
        .withWidth(90);
    builder.for(ContractColumn.erpId, localize("Column_Header_ErpId"), "erpDisplayId", localize("Column_Description_ErpId"))
        .withGetter(params => {
            const data = params.data;
            if (!data) return null;
            return {
                erpDisplayId: data.erpDisplayId,
                isErpInSync: data.isErpInSync,
                isErpIdManuallyEntered: data.isErpIdManuallyEntered,
                isErpIdVerified: data.isErpIdVerified
            };
        })
        .withSearchFilter(params => params.data?.erpDisplayId ?? "")
        .withCellRenderer(params => <ErpId
            value={params.value?.erpDisplayId}
            hasError={params.value?.isErpInSync === false}
            isManuallyEntered={params.value?.isErpIdManuallyEntered}
            isVerified={params.value?.isErpIdVerified}
            onSelected={() => filter.onErpIdSelected(params.node.data)} />)
        .withWidth(125);
    builder.for(ContractColumn.eSignStatus, localize("Column_Header_ESignStatus"), null, localize("Column_Description_ESignStatus"), { hide: !filter.isEsignEnabled })
        .withGetter(params => getContractSignatureStatusText(params.data, filter.isCustomer))
        .withCellRenderer(params => <ESignStatus
            status={params.value}
            contract={params.node.data}
            onSelected={() => filter.onSignatureRequested(params.node.data)} isCustomer={filter.isCustomer} />)
        .withCellClass(params => getContractSignatureStatusTextClass(params.data, filter.isCustomer))
        .withWidth(90);

    builder.for(ContractColumn.fillPrice, localize("Column_Header_OrderFilledPrice"), "offer.order.fillPrice", localize("Column_Description_OrderFilledPrice"), { hide: filter.isCustomer })
        .asDecimal()
        .withWidth(80);
    builder.for(ContractColumn.producerAccount, localize("Column_Header_AccountId"), "", localize("Column_Description_AccountId"))
        .withGetter(params => getProducerAccountDisplayName(params.data?.customerAccount))
        .withWidth(160);
    if (filter.merchant.generalSettings.displayPaymentOption) {
        builder.for(ContractColumn.paymentOption, localize("Column_Header_PaymentOption"), "paymentOption", localize("Column_Description_PaymentOption"))
            .withFormatter(filter.translators.paymentOptions.translate)
            .withWidth(120);
    }
    builder.for(ContractColumn.contractType, localize("Column_Header_Type"), "contractType", localize("Column_Description_ContractType"))
        .withFormatter(getContractTypeName)
        .withWidth(100);
    return builder;
}

interface ErpIdProps {
    value: string;
    hasError: boolean;
    isManuallyEntered?: boolean;
    isVerified?: boolean;
    onSelected(): void;
}

const ErpId: React.FC<ErpIdProps> = React.memo(props => {
    const canRetrySync = useAnyPermission(Permissions.ManageErpMapping, Permissions.RetrySync);

    // show red font and warning icon for manually entered unverified IDs or ERP errors
    const isUnverified = props.isManuallyEntered && !props.isVerified;
    const showWarning = props.hasError || isUnverified;
    const isSyncing = isErpIdSyncing(props.value);

    if (!canRetrySync) {
        // user doesn't have permission - show visual indicator but no interaction
        return <span className={(showWarning || isSyncing) ? "text-danger" : undefined}>
            {props.value}
            {showWarning && <i className="pl-1 fa fa-exclamation-triangle"></i>}
        </span>;
    }

    if (!showWarning) {
        // has permission but no warning - just show the value, no interaction needed
        return <span className={isSyncing ? "text-danger" : undefined}>{props.value}</span>;
    }

    // has permission and has warning - show clickable button
    return <button className={classNames('link-button', { 'text-danger': showWarning })} onClick={props.onSelected}>
        {props.value !== "Error" ? props.value : null}
        <i className="pl-1 fa fa-exclamation-triangle"></i>
    </button>
});

interface FlagCheckboxProps {
    isChecked: boolean;
    onChange(isChecked: boolean): void;
    hide: boolean;
}

const FlagCheckbox: React.FC<FlagCheckboxProps> = React.memo(props => {

    const isChecked = useRef(props.isChecked);
    const id = useUniqueId();

    function onChange(newVal: boolean) {
        isChecked.current = newVal;
        props.onChange(newVal);
    }

    if (props.hide) return <></>;

    return <Form.Check
        id={id}
        custom
        label=""
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange(event.target.checked)}
        checked={isChecked.current} />;
});

export interface ContractColumnConfigurations {
    basis: ColumnCustomization;
    cash: ColumnCustomization;
    hta: ColumnCustomization;
    delayedPrice: ColumnCustomization;
    withOptions: ColumnCustomization;
    bushelOnly: ColumnCustomization;
}

export const defaultContractColumns: ContractColumnConfigurations = {
    basis: {
        preferredSortColumnId: null,
        preferredSortOrder: null,
        visibleColumns: [
            ContractColumn.id,
            ContractColumn.customerContractId,
            ContractColumn.flag,
            ContractColumn.erpId,
            ContractColumn.eSignStatus,
            ContractColumn.customer,
            ContractColumn.producerAccount,
            ContractColumn.commodity,
            ContractColumn.position,
            ContractColumn.location,
            ContractColumn.inventoryLocation,
            ContractColumn.contractLocation,
            ContractColumn.customerWillSell,
            ContractColumn.quantity,
            ContractColumn.workingQuantity,
            ContractColumn.remainingQuantity,
            ContractColumn.appliedQuantity,
            ContractColumn.unappliedQuantity,
            ContractColumn.basis,
            ContractColumn.contractSymbol,
            ContractColumn.deliveryPeriod,
            ContractColumn.trader,
            ContractColumn.lastUpdatedDate
        ]
    },
    cash: {
        preferredSortColumnId: null,
        preferredSortOrder: null,
        visibleColumns: [
            ContractColumn.id,
            ContractColumn.customerContractId,
            ContractColumn.flag,
            ContractColumn.erpId,
            ContractColumn.eSignStatus,
            ContractColumn.orderId,
            ContractColumn.customer,
            ContractColumn.producerAccount,
            ContractColumn.commodity,
            ContractColumn.position,
            ContractColumn.location,
            ContractColumn.inventoryLocation,
            ContractColumn.contractLocation,
            ContractColumn.customerWillSell,
            ContractColumn.quantity,
            ContractColumn.appliedQuantity,
            ContractColumn.unappliedQuantity,
            ContractColumn.cashPrice,
            ContractColumn.futuresPrice,
            ContractColumn.currency,
            ContractColumn.finalPrice,
            ContractColumn.fillPrice,
            ContractColumn.actualBasis,
            ContractColumn.myBasis,
            ContractColumn.contractSymbol,
            ContractColumn.deliveryPeriod,
            ContractColumn.trader,
            ContractColumn.fillTime,
            ContractColumn.lastUpdatedDate
        ]
    },
    hta: {
        preferredSortColumnId: null,
        preferredSortOrder: null,
        visibleColumns: [
            ContractColumn.id,
            ContractColumn.customerContractId,
            ContractColumn.flag,
            ContractColumn.erpId,
            ContractColumn.eSignStatus,
            ContractColumn.orderId,
            ContractColumn.customer,
            ContractColumn.producerAccount,
            ContractColumn.commodity,
            ContractColumn.position,
            ContractColumn.location,
            ContractColumn.inventoryLocation,
            ContractColumn.contractLocation,
            ContractColumn.customerWillSell,
            ContractColumn.quantity,
            ContractColumn.workingQuantity,
            ContractColumn.remainingQuantity,
            ContractColumn.appliedQuantity,
            ContractColumn.unappliedQuantity,
            ContractColumn.futuresPrice,
            ContractColumn.currency,
            ContractColumn.finalPrice,
            ContractColumn.fillPrice,
            ContractColumn.contractSymbol,
            ContractColumn.deliveryPeriod,
            ContractColumn.totalFee,
            ContractColumn.trader,
            ContractColumn.fillTime,
            ContractColumn.lastUpdatedDate
        ]
    },
    delayedPrice: {
        preferredSortColumnId: null,
        preferredSortOrder: null,
        visibleColumns: [
            ContractColumn.id,
            ContractColumn.customerContractId,
            ContractColumn.flag,
            ContractColumn.erpId,
            ContractColumn.eSignStatus,
            ContractColumn.customer,
            ContractColumn.producerAccount,
            ContractColumn.commodity,
            ContractColumn.location,
            ContractColumn.inventoryLocation,
            ContractColumn.contractLocation,
            ContractColumn.customerWillSell,
            ContractColumn.quantity,
            ContractColumn.workingQuantity,
            ContractColumn.remainingQuantity,
            ContractColumn.appliedQuantity,
            ContractColumn.unappliedQuantity,
            ContractColumn.trader,
            ContractColumn.finalPricingDate,
            ContractColumn.createdDate,
            ContractColumn.lastUpdatedDate
        ]
    },
    withOptions: {
        preferredSortColumnId: null,
        preferredSortOrder: null,
        visibleColumns: [
            ContractColumn.id,
            ContractColumn.customerContractId,
            ContractColumn.flag,
            ContractColumn.contractType,
            ContractColumn.erpId,
            ContractColumn.eSignStatus,
            ContractColumn.orderId,
            ContractColumn.customer,
            ContractColumn.producerAccount,
            ContractColumn.commodity,
            ContractColumn.position,
            ContractColumn.location,
            ContractColumn.inventoryLocation,
            ContractColumn.contractLocation,
            ContractColumn.customerWillSell,
            ContractColumn.quantity,
            ContractColumn.appliedQuantity,
            ContractColumn.unappliedQuantity,
            ContractColumn.cashPrice,
            ContractColumn.futuresPrice,
            ContractColumn.finalPrice,
            ContractColumn.totalFee,
            ContractColumn.fillPrice,
            ContractColumn.actualBasis,
            ContractColumn.myBasis,
            ContractColumn.contractSymbol,
            ContractColumn.deliveryPeriod,
            ContractColumn.trader,
            ContractColumn.fillTime,
            ContractColumn.lastUpdatedDate
        ]
    },
    bushelOnly: {
        preferredSortColumnId: null,
        preferredSortOrder: null,
        visibleColumns: [
            ContractColumn.id,
            ContractColumn.customerContractId,
            ContractColumn.flag,
            ContractColumn.contractExtendedType,
            ContractColumn.contractSymbol,
            ContractColumn.erpId,
            ContractColumn.customer,
            ContractColumn.producerAccount,
            ContractColumn.commodity,
            ContractColumn.location,
            ContractColumn.contractLocation,
            ContractColumn.customerWillSell,
            ContractColumn.quantity,
            ContractColumn.workingQuantity,
            ContractColumn.remainingQuantity,
            ContractColumn.trader,
            ContractColumn.createdDate,
            ContractColumn.lastUpdatedDate
        ]
    }
}


export const mobileColumns: ContractColumnConfigurations = {
    basis: {
        preferredSortColumnId: null,
        preferredSortOrder: null,
        visibleColumns: [
            ContractColumn.id,
            ContractColumn.customer,
            ContractColumn.quantity,
            ContractColumn.remainingQuantity_mobile,
            ContractColumn.basis,
            ContractColumn.contractSymbol
        ]
    },
    cash: {
        preferredSortColumnId: null,
        preferredSortOrder: null,
        visibleColumns: [
            ContractColumn.id,
            ContractColumn.customer,
            ContractColumn.quantity,
            ContractColumn.cashPrice,
            ContractColumn.contractSymbol
        ]
    },
    hta: {
        preferredSortColumnId: null,
        preferredSortOrder: null,
        visibleColumns: [
            ContractColumn.id,
            ContractColumn.customer,
            ContractColumn.quantity,
            ContractColumn.remainingQuantity_mobile,
            ContractColumn.futuresPrice,
            ContractColumn.contractSymbol
        ]
    },
    delayedPrice: {
        preferredSortColumnId: null,
        preferredSortOrder: null,
        visibleColumns: [
            ContractColumn.id,
            ContractColumn.customer,
            ContractColumn.quantity,
            ContractColumn.remainingQuantity_mobile
        ]
    },
    withOptions: {
        preferredSortColumnId: null,
        preferredSortOrder: null,
        visibleColumns: [
            ContractColumn.id,
            ContractColumn.customer,
            ContractColumn.quantity,
            ContractColumn.cashPrice,
            ContractColumn.contractSymbol
        ]
    },
    bushelOnly: {
        preferredSortColumnId: null,
        preferredSortOrder: null,
        visibleColumns: [
            ContractColumn.id,
            ContractColumn.contractExtendedType,
            ContractColumn.contractSymbol,
            ContractColumn.customer,
            ContractColumn.quantity,
            ContractColumn.remainingQuantity_mobile
        ]
    }
}
